import { AfterViewInit, Component, ElementRef, Input, OnChanges, QueryList, Renderer2, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { commonComponentImages } from '@images/common-components-images';
import { commonImages } from '@images/common-images';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { ApiService } from '@services/api/api.service';
import { ListingApprovalService } from '@services/api/listing-approval.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { Utils } from '@services/helpers/utils';
import { SharedDataService } from '@services/shared-data.service';
import { ToasterService } from '@services/util/toaster.service';
import { RootReducerState, Store, getLoggedInUserStatus, select } from '@state-management/reducers';
import moment from 'moment';
import { OverlayPanel } from 'primeng/overlaypanel';
import { IActivePost, IPostComment } from '../listing-preview-models';

@Component({
  selector: 'app-highlight-overlay-bubbles',
  templateUrl: './highlight-overlay-bubbles.component.html',
  styleUrls: ['./highlight-overlay-bubbles.component.scss'],
})
export class HighlightOverlayBubblesComponent implements OnChanges, AfterViewInit {
  Utils = Utils; // 👈 expose Utils class to the template
  public userRole = Crypto.decrypt(this.localStorageService.GetItem('role'));
  public scrollContainer: HTMLElement;
  @ViewChildren('bubbleContainer') bubbleContainers: QueryList<ElementRef>;
  @Input() cb_afterupdate: (value: any) => void;
  @Input() lisitngDetail: any = null;
  @Input() bubbles: IActivePost[] = [];
  @Input() readWritePermissions: { can_delete: boolean; can_write: boolean; can_approve: boolean; can_resolve: boolean } = {
    can_approve: false,
    can_delete: false,
    can_resolve: false,
    can_write: false,
  };
  @ViewChild('op') overlaypanel: OverlayPanel;
  active_bubble_post: IActivePost = null;
  message: string = '';
  public Editor = ClassicEditor;
  editableComment: IPostComment = null;
  images = commonComponentImages;
  common_images = commonImages;

  constructor(
    private api: ApiService,
    private sharedDataService: SharedDataService,
    private store: Store<RootReducerState>,
    public toasterService: ToasterService,
    private renderer: Renderer2,
    public el: ElementRef,
    private localStorageService: LocalStorageService,
    private _listingApprovalService: ListingApprovalService,
    private remmiConfirm: RemmiConfirmService
  ) {
    this.sharedDataService.setOverlayBubbleComponent(this);
  }

  /**
   * Detects changes in component inputs and updates the bubbles array accordingly.
   * Also scrolls the container slightly to trigger UI updates.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.bubbles?.currentValue) {
      this.bubbles =
        changes.bubbles?.currentValue?.map((value, index) => {
          value = { ...value, label: value?.label ? value?.label : index + 1 };
          return value;
        }) || [];
      setTimeout(() => {
        this.scrollContainer?.scrollBy(0, 50);
        this.scrollContainer?.scrollBy(0, -50);
      }, 1000);
    }
  }

  /**
   * Lifecycle hook that is called after the view has been initialized.
   * Attaches a scroll event listener.
   */
  ngAfterViewInit(): void {
    this.attachScrollListener();
  }

  /**
   * Attaches a scroll listener to either the pop-up modal or the body.
   * This helps in tracking and updating bubble positions.
   */
  attachScrollListener() {
    let popModal = this.el.nativeElement.querySelector('.bubble-outer-container.referenced-bubble .confirmation-dialog-container');
    let bodyModal = document.querySelector('body');
    if (popModal) {
      this.scrollContainer = popModal;
      this.renderer.listen(this.scrollContainer, 'scroll', () => {
        this.reassignBubbleContainer();
      });
    } else if (bodyModal) {
      this.scrollContainer = bodyModal;
      document.addEventListener('scroll', () => {
        this.reassignBubbleContainer();
      });
    }
  }

  /**
   * Reassigns bubble containers by appending them to their respective DOM elements.
   * Also hides the overlay panel after reassignment.
   */
  reassignBubbleContainer() {
    this.bubbleContainers?.map(node => {
      let splittedNode: string[] = node.nativeElement.id?.split('_');
      let findItem = this.bubbles?.find(comment => comment?.id == splittedNode?.[splittedNode?.length - 1]);
      if (findItem) {
        let appendToElem = document.getElementById(findItem?.id);
        appendToElem && appendToElem.appendChild(node.nativeElement);
      }
      this.overlaypanel.hide();
    });
  }

  /**
   * Triggers the overlay panel with the provided post information.
   * Hides the panel first, assigns the active post, and then shows the panel after a short delay.
   */
  triggerOverlayPanel(event, post: IActivePost) {
    this.overlaypanel.hide();
    this.active_bubble_post = post;
    this.message = '';
    setTimeout(() => {
      this.overlaypanel.show(event);
    }, 500);
  }

  /**
   * Enables editing of a comment by setting it as the editable comment.
   * Also assigns the message content to the editable comment.
   */
  onEditComment(comment: IPostComment) {
    this.message = comment.message || '';
    this.editableComment = comment;
  }

  confirmRemoveComment(comment: IPostComment) {
    this.remmiConfirm
      .delete({ title: 'Delete Comment?', message: 'This comment will be permanently deleted.' })
      .subscribe(isConfirmed => isConfirmed && this.createComment(comment?.id, 'remove'));
  }

  /**
   * Creates, updates, or removes a comment within the active bubble post.
   *
   * @param id - The ID of the comment to modify (empty for new comments)
   * @param addRemove - The type of modification: 'add', 'remove', or 'update'
   */
  createComment(id: string = '', addRemove: 'add' | 'remove' | 'update' = 'add') {
    this.store.pipe(select(getLoggedInUserStatus)).subscribe({
      next: response => {
        let modification_in: { type: 'add' | 'remove' | 'update'; data: any } = null;
        if (response) {
          // Adding a new comment
          if (id?.length === 0 && !this.editableComment?.id) {
            let newComment = {
              id: Utils.generateUUID(),
              commented_by: ((response?.first_name || '') + ' ' + (response?.last_name || '')).trim(),
              logo: response?.photo_thumb || '',
              message: this.message,
              user_id: response?._id,
              is_resolved_by_admin: false,
              created_at: moment().format('DD-MM-YYYY hh:mm:ss A'),
              is_resolved: false,
            };
            this.active_bubble_post.comments.push(newComment);
            modification_in = { type: addRemove, data: newComment };
          }
          // Removing an existing comment
          else if (id?.length > 0 && addRemove === 'remove') {
            let filteredComments = this.active_bubble_post.comments?.filter(localCommentData => localCommentData?.id !== id);
            modification_in = { type: addRemove, data: this.active_bubble_post.comments?.find(localCommentData => localCommentData?.id === id) };
            if (filteredComments?.length === 0) {
              let findIndexToRemove = this.bubbles.findIndex(bubble => bubble.reference_id === this.active_bubble_post.reference_id);
              let singleBubbleBox = document.querySelectorAll(
                `[data-bubble-ref="${this.bubbles?.[findIndexToRemove]?.reference_id}"]`
              ) as NodeListOf<Element>;
              singleBubbleBox && singleBubbleBox?.forEach(el => el?.remove());
              this.bubbles.splice(findIndexToRemove, 1);
            } else {
              this.active_bubble_post.comments = filteredComments || [];
            }
          }
          // Updating an existing comment
          else if (this.editableComment?.id) {
            modification_in = { type: 'update', data: { ...this.editableComment, message: this.message } };
            this.active_bubble_post.comments = this.active_bubble_post.comments?.map(result => {
              if (result.id === this.editableComment.id) {
                return modification_in?.data;
              }
              return result;
            });
          }

          // Preparing request parameters
          let param = {
            modification: modification_in,
            office_id: this.api.getOfficeId(),
            listing_id: this.lisitngDetail?._id || '',
            listing_comments: this.bubbles?.map(result => {
              if (result?.reference_id === this.active_bubble_post?.reference_id) {
                return (this.active_bubble_post = {
                  ...this.active_bubble_post,
                  comments: this.active_bubble_post.comments?.map(localComment => {
                    return { ...localComment, is_resolved: this.active_bubble_post.is_resolved };
                  }),
                });
              }
              return result;
            }),
          };

          // Sending API request to save comment changes
          this._listingApprovalService.saveCommentsOnListingApproval(param).subscribe({
            next: response => {
              const { status, data } = response;
              if (status && data !== null) {
                this.cb_afterupdate(data);
                this.toasterService.Success('Saved successfully.');
              } else {
                this.toasterService.Error('Something went wrong');
              }
            },
            error: () => {
              this.toasterService.Error('Something went wrong');
            },
            complete: () => {
              this.message = '';
              this.editableComment = null;
            },
          });
        }
      },
    });
  }

  /**
   * Converts a date string into a JavaScript Date object using the format 'DD-MM-YYYY'.
   * Returns an empty string if the value is invalid.
   */
  getMomentDateTime(value: any): any {
    try {
      if (value) {
        return moment(value, 'DD-MM-YYYY').toDate();
      }
      return '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Handles clicks outside the overlay panel. If the clicked element is not part of the overlay,
   * the overlay panel will be hidden.
   */
  overlayPanelOutsideClicked(event) {
    const { bol, elementtarget } = event as { bol: boolean; elementtarget: HTMLElement };
    if (bol) {
      elementtarget?.classList?.contains('p-overlaypanel-content') ? null : this.overlaypanel.hide();
    }
  }

  /**
   * Changes the status of a comment to resolved or unresolved. Also updates the admin resolution status
   * based on the user's role and triggers an update to the comment.
   */
  onChangeCommentStatus(comment: IPostComment) {
    this.active_bubble_post.is_resolved = !this.active_bubble_post?.is_resolved;
    this.active_bubble_post.is_resolved_by_admin = this.userRole === 'master admin' || this.userRole === 'operations manager' ? true : false;
    this.editableComment = this.active_bubble_post?.comments?.find(_comment => _comment.id === comment.id);
    this.editableComment = {
      ...this.editableComment,
      is_resolved: this.active_bubble_post.is_resolved,
      is_resolved_by_admin: this.active_bubble_post.is_resolved_by_admin,
    };
    this.message = this.editableComment.message;
    this.createComment(comment?.id, 'update');
  }
}
